import {isClientOnly} from './clientOnly'

import type {CapabilityContext, ResolvedCapabilities} from './types/core'
import type {BrandCapabilities} from './types/domains'

interface ResolveCapabilitiesOptions {
  /**
   * When true, capabilities wrapped in `clientOnly(...)` are omitted from
   * the resolved output. Use at SSR time — the resulting snapshot is safe
   * to cross the JSON boundary and carries only values the server can
   * actually produce. The hook path resolves these keys normally on the
   * client.
   */
  skipClientOnly?: boolean
}

/**
 * Walks the capabilities tree and resolves each value: static values
 * pass through, resolver functions are called with `ctx`, and
 * `ClientOnly` markers are either resolved (default) or skipped
 * (`skipClientOnly: true`). Pure function — testable outside React.
 *
 * `Ctx` is generic so the caller's narrower context types (e.g.
 * `SSRCapabilityContext` at SSR) type-check without a cast. At runtime
 * the walker only passes `ctx` through to resolvers — it doesn't read
 * any fields itself — so any shape the caller asserts is safe.
 *
 * `ctx` is optional: callers that only read capabilities shipping as
 * static values (e.g. Redux selectors running outside React) can omit
 * it. Resolvers or `ClientOnly` values encountered without a context
 * are invoked with `undefined` — fine for resolvers that don't read
 * `ctx`, and throws at resolve time for ones that do.
 */
export const resolveCapabilities = <Ctx>(
  capabilities: BrandCapabilities,
  ctx?: Ctx,
  {skipClientOnly = false}: ResolveCapabilitiesOptions = {}
): ResolvedCapabilities => {
  const resolved: Record<string, Record<string, unknown>> = {}

  for (const domain of Object.keys(capabilities)) {
    const domainBlock = capabilities[
      domain as keyof BrandCapabilities
    ] as Record<string, unknown>
    const resolvedDomain: Record<string, unknown> = {}

    for (const key of Object.keys(domainBlock)) {
      const value = domainBlock[key]

      if (isClientOnly<unknown>(value)) {
        if (skipClientOnly) continue
        const resolvedValue = value.resolve(ctx as CapabilityContext)
        // Omit `undefined` resolutions: an absent key reads back as
        // `undefined` for consumers (matching `T | undefined` capability
        // types) while keeping the snapshot JSON-safe across the SSR boundary,
        // where Next.js rejects explicit `undefined` props.
        if (resolvedValue !== undefined) {
          resolvedDomain[key] = resolvedValue
        }
        continue
      }

      const resolvedValue =
        typeof value === 'function'
          ? (value as (ctx: Ctx) => unknown)(ctx as Ctx)
          : value
      // See above: never assign `undefined` into the resolved snapshot.
      if (resolvedValue !== undefined) {
        resolvedDomain[key] = resolvedValue
      }
    }

    resolved[domain] = resolvedDomain
  }

  return resolved as ResolvedCapabilities
}
